import { useMemo, useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react'
import { Canvas, useThree, type ThreeEvent } from '@react-three/fiber'
import { OrbitControls, GizmoHelper, GizmoViewport, Html } from '@react-three/drei'
import { motion, AnimatePresence } from 'framer-motion'
import { Maximize2, Minimize2 } from 'lucide-react'
import * as THREE from 'three'
import { cncToScene, sceneToCnc, computeSnapPoints, resolveSnapPoint } from '../../lib/snap-points'
import { CameraRig } from './CameraRig'
import { OriginPickerPopup, REGISTERS, type WorkOffsetRegister } from './OriginPickerPopup'
import occtimportjs from 'occt-import-js'

const SnapPointsLayer = lazy(() => import('./SnapPointsLayer'))

// ---- Feature visualization helpers ----

type FeatureType = 'profile' | 'pocket' | 'hole' | 'slot' | 'chamfer' | 'fillet' | 'bore' | 'thread' | 'face' | 'step' | 'taper' | 'other'

function featureColor(type: FeatureType): string {
  switch (type) {
    case 'hole': case 'bore': case 'thread': return '#06b6d4'
    case 'pocket': case 'slot': return '#3b82f6'
    case 'profile': return '#f97316'
    case 'chamfer': case 'fillet': return '#fbbf24'
    default: return '#94a3b8'
  }
}

// ---- STEP mesh types and renderer ----

interface OcctMesh {
  name: string
  color?: [number, number, number]
  brep_faces: Array<{ first: number; last: number; color: [number, number, number] | null }>
  attributes: { position: { array: number[] }; normal?: { array: number[] } }
  index: { array: number[] }
}

/** Adapt a backend-built solid mesh (e.g. a turned DWG part from the CAM service)
 *  into the OcctMesh shape so it renders through the same path as STEP meshes. */
function solidMeshToOcct(
  sm: { vertices: number[]; indices: number[]; normals?: number[] },
): OcctMesh {
  return {
    name: 'solid',
    brep_faces: [],
    attributes: {
      position: { array: sm.vertices },
      ...(sm.normals && sm.normals.length ? { normal: { array: sm.normals } } : {}),
    },
    index: { array: sm.indices },
  }
}

function StepMesh({ stepMeshes, wireframe = false }: { stepMeshes: OcctMesh[]; wireframe?: boolean }) {
  // Track previous GPU resources for cleanup
  const prevResourcesRef = useRef<{ geometries: THREE.BufferGeometry[]; materials: THREE.Material[] }>({ geometries: [], materials: [] })

  const { meshObjects, edgesObjects } = useMemo(() => {
    // Dispose previous GPU resources before creating new ones
    for (const g of prevResourcesRef.current.geometries) g.dispose()
    for (const m of prevResourcesRef.current.materials) m.dispose()
    prevResourcesRef.current = { geometries: [], materials: [] }

    const meshes: Array<{ geometry: THREE.BufferGeometry; materials: THREE.MeshPhongMaterial[] }> = []
    const edges: THREE.BufferGeometry[] = []

    for (const mesh of stepMeshes) {
      const geo = new THREE.BufferGeometry()

      const posArr = new Float32Array(mesh.attributes.position.array)
      geo.setAttribute('position', new THREE.BufferAttribute(posArr, 3))

      if (mesh.attributes.normal) {
        const normArr = new Float32Array(mesh.attributes.normal.array)
        geo.setAttribute('normal', new THREE.BufferAttribute(normArr, 3))
      }

      const idxArr = new Uint32Array(mesh.index.array)
      geo.setIndex(new THREE.BufferAttribute(idxArr, 1))

      if (!mesh.attributes.normal) {
        geo.computeVertexNormals()
      }

      // Build per-face materials using brep_faces
      const materials: THREE.MeshPhongMaterial[] = []
      const defaultColor = mesh.color
        ? new THREE.Color(mesh.color[0] / 255, mesh.color[1] / 255, mesh.color[2] / 255)
        : new THREE.Color(0.7, 0.7, 0.75)

      if (mesh.brep_faces && mesh.brep_faces.length > 0) {
        for (const face of mesh.brep_faces) {
          const faceColor = face.color
            ? new THREE.Color(face.color[0] / 255, face.color[1] / 255, face.color[2] / 255)
            : defaultColor
          const mat = new THREE.MeshPhongMaterial({
            color: faceColor,
            specular: new THREE.Color(0x111111),
            shininess: 20,
            side: THREE.DoubleSide,
          })
          // brep_faces first/last are triangle indices; Three.js addGroup needs index-buffer offsets (3 per tri)
          const groupStart = face.first * 3
          const groupCount = (face.last - face.first + 1) * 3
          geo.addGroup(groupStart, groupCount, materials.length)
          materials.push(mat)
        }
      } else {
        // No brep_faces, single material
        materials.push(new THREE.MeshPhongMaterial({
          color: defaultColor,
          specular: new THREE.Color(0x111111),
          shininess: 20,
          side: THREE.DoubleSide,
        }))
      }

      meshes.push({ geometry: geo, materials })

      // Sharp BRep edges (angle 15 degrees — only show real edges, not tessellation artifacts)
      const edgeGeo = new THREE.EdgesGeometry(geo, 15)
      edges.push(edgeGeo)
    }

    // Track for disposal on next update or unmount
    prevResourcesRef.current = {
      geometries: [...meshes.map(m => m.geometry), ...edges],
      materials: meshes.flatMap(m => m.materials),
    }

    return { meshObjects: meshes, edgesObjects: edges }
  }, [stepMeshes])

  // Dispose GPU resources on unmount
  useEffect(() => () => {
    for (const g of prevResourcesRef.current.geometries) g.dispose()
    for (const m of prevResourcesRef.current.materials) m.dispose()
  }, [])

  return (
    <group>
      {/* Solid mode: shaded faces + faint dark edges. Wireframe mode: hide faces and show the
          clean BRep edges in a light color so they read against the dark background. */}
      {!wireframe && meshObjects.map((m, i) => (
        <mesh key={`step-mesh-${i}`} geometry={m.geometry} material={m.materials.length === 1 ? m.materials[0] : m.materials} />
      ))}
      {edgesObjects.map((e, i) => (
        <lineSegments key={`step-edge-${i}`} geometry={e}>
          <lineBasicMaterial
            color={wireframe ? '#94a3b8' : '#1a1a1a'}
            linewidth={1}
            transparent
            opacity={wireframe ? 0.9 : 0.3}
          />
        </lineSegments>
      ))}
    </group>
  )
}

// ---- Mesh bounding box helper ----

function computeMeshBounds(meshes: OcctMesh[]): {
  center: [number, number, number]
  extent: number
  min: [number, number, number]
  max: [number, number, number]
} {
  let minX = Infinity, minY = Infinity, minZ = Infinity
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
  for (const mesh of meshes) {
    const pos = mesh.attributes.position.array
    for (let i = 0; i < pos.length; i += 3) {
      if (pos[i] < minX) minX = pos[i]
      if (pos[i + 1] < minY) minY = pos[i + 1]
      if (pos[i + 2] < minZ) minZ = pos[i + 2]
      if (pos[i] > maxX) maxX = pos[i]
      if (pos[i + 1] > maxY) maxY = pos[i + 1]
      if (pos[i + 2] > maxZ) maxZ = pos[i + 2]
    }
  }
  return {
    center: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2],
    extent: Math.max(maxX - minX, maxY - minY, maxZ - minZ, 20),
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
  }
}

/**
 * CNC-space footprint (bounds + center) of the rendered 2D features (contours, holes, edges).
 * Used to place the stock box AND dimension annotations over the part when there's no STEP
 * mesh — the extractor's feature frame isn't always origin-centered, so we can't assume the
 * part sits at (0,0). Returns null when there are no positioned features.
 */
function computeFeatureFootprint(geometry: Record<string, unknown>):
  | { center: { x: number; y: number }; min: { x: number; y: number }; max: { x: number; y: number } }
  | null {
  const features = (geometry.features ?? []) as Array<{ geometry?: any }>
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  const add = (x: unknown, y: unknown) => {
    if (typeof x !== 'number' || typeof y !== 'number') return
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  for (const f of features) {
    const g = f.geometry
    if (!g) continue
    if (Array.isArray(g.points)) for (const p of g.points) add(p.x, p.y)
    if (Array.isArray(g.locations)) for (const l of g.locations) add(l.x, l.y)
    if (g.center_x !== undefined) add(g.center_x, g.center_y)
    if (g.start_x !== undefined) { add(g.start_x, g.start_y); add(g.end_x, g.end_y) }
  }
  if (minX === Infinity) return null
  return {
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    min: { x: minX, y: minY },
    max: { x: maxX, y: maxY },
  }
}

// ---- 3D Scene sub-components ----

export type PlaneView = 'iso' | 'top' | 'front' | 'right'

function StockBox({ geometry, meshCenter, footprintCenter }: { geometry?: Record<string, unknown> | null; meshCenter?: [number, number, number] | null; footprintCenter?: { x: number; y: number } | null }) {
  const boxData = useMemo(() => {
    if (!geometry) return null
    const stock = geometry.stock_dimensions as { x?: number; y?: number; z?: number } | undefined
    if (!stock?.x || !stock?.y) return null
    const sz = stock.z || 0
    return { x: stock.x, y: stock.y, z: sz }
  }, [geometry])

  if (!boxData) return null

  // Center the stock over the part. Priority:
  // 1. STEP mesh center (3D path) — exact mesh position.
  // 2. footprintCenter (2D path) — the part's actual CNC footprint center, which is NOT
  //    necessarily the origin (the extractor's feature frame can be offset from the envelope).
  // 3. origin fallback.
  const pos: [number, number, number] = meshCenter
    ? [meshCenter[0], meshCenter[1], meshCenter[2]]
    : footprintCenter
      ? [footprintCenter.x, boxData.z / 2, -footprintCenter.y]
      : [0, boxData.z / 2, 0]

  return (
    <mesh position={pos}>
      <boxGeometry args={[boxData.x, boxData.z, boxData.y]} />
      <meshBasicMaterial color="#06b6d4" wireframe opacity={0.15} transparent depthWrite={false} />
    </mesh>
  )
}

function ContourOutline({ points, y, color, opacity = 0.8 }: { points: Array<{ x: number; y: number }>; y: number; color: string; opacity?: number }) {
  const geom = useMemo(() => {
    if (!points?.length || points.length < 2) return null
    const verts: number[] = []
    for (let i = 0; i < points.length; i++) {
      const a = points[i]
      const b = points[(i + 1) % points.length]
      verts.push(a.x, y, -a.y, b.x, y, -b.y)
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3))
    return g
  }, [points, y])

  if (!geom) return null

  return (
    <lineSegments geometry={geom} renderOrder={1}>
      <lineBasicMaterial color={color} opacity={opacity} transparent depthWrite={false} />
    </lineSegments>
  )
}

function VerticalEdges({ points, yTop, yBottom, color, opacity = 0.5 }: { points: Array<{ x: number; y: number }>; yTop: number; yBottom: number; color: string; opacity?: number }) {
  const geom = useMemo(() => {
    if (!points?.length) return null
    const verts: number[] = []
    for (const p of points) {
      verts.push(p.x, yTop, -p.y, p.x, yBottom, -p.y)
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3))
    return g
  }, [points, yTop, yBottom])

  if (!geom) return null

  return (
    <lineSegments geometry={geom} renderOrder={1}>
      <lineBasicMaterial color={color} opacity={opacity} transparent depthWrite={false} />
    </lineSegments>
  )
}

function ContourMesh({ points, depth, color, isProfile }: { points: Array<{ x: number; y: number }>; depth: number; color: string; isProfile?: boolean }) {
  const d = Math.abs(depth)
  const edgeColor = isProfile ? '#e2e8f0' : color

  if (!points?.length || points.length < 2) return null

  return (
    <group>
      <ContourOutline points={points} y={d} color={edgeColor} opacity={isProfile ? 0.9 : 0.7} />
      <ContourOutline points={points} y={0} color={edgeColor} opacity={isProfile ? 0.5 : 0.4} />
      <VerticalEdges points={points} yTop={d} yBottom={0} color={edgeColor} opacity={isProfile ? 0.6 : 0.4} />
    </group>
  )
}

function EdgeStripLine({ geom, color }: { geom: { start_x: number; start_y: number; end_x: number; end_y: number; depth?: number }; color: string }) {
  const lineGeom = useMemo(() => {
    const g = new THREE.BufferGeometry()
    const d = geom.depth ? -Math.abs(geom.depth) : 0
    g.setAttribute('position', new THREE.Float32BufferAttribute([
      geom.start_x, d, -geom.start_y,
      geom.end_x, d, -geom.end_y,
    ], 3))
    return g
  }, [geom])

  return (
    <lineSegments geometry={lineGeom} renderOrder={1}>
      <lineBasicMaterial color={color} linewidth={2} opacity={0.6} transparent depthWrite={false} />
    </lineSegments>
  )
}

function FeatureOverlay({ geometry }: { geometry: Record<string, unknown> }) {
  const features = (geometry.features ?? []) as Array<{
    name: string; type: string; geometry: any; notes?: string
  }>
  // part_depth = finished part height; stock_dimensions.z = raw stock (thicker)
  const partDepth = geometry.part_depth as number | undefined
  const stock = geometry.stock_dimensions as { z?: number } | undefined
  const fallbackDepth = partDepth || stock?.z || 0

  if (!features.length) return null

  return (
    <group>
      {features.flatMap((feature, fi) => {
        const g = feature.geometry
        if (!g?.shape) return []
        const color = featureColor(feature.type as FeatureType)

        if (g.shape === 'face_plane') return []

        if (g.shape === 'cylinder') {
          const r = (g.diameter as number) / 2
          const depth = Math.abs(g.depth as number) || fallbackDepth
          const locs: Array<{ x: number; y: number }> = g.locations || [{ x: g.center_x, y: g.center_y }]
          return locs.flatMap((loc, li) => [
            <mesh key={`cyl-edge-${fi}-${li}`} position={[loc.x, depth / 2, -loc.y]} renderOrder={1}>
              <cylinderGeometry args={[r, r, depth, 32]} />
              <meshBasicMaterial color={color} wireframe opacity={0.4} transparent depthWrite={false} />
            </mesh>,
          ])
        }

        if (g.shape === 'box') {
          const depth = Math.abs(g.depth as number) || fallbackDepth
          const locs: Array<{ x: number; y: number }> = g.locations || [{ x: g.center_x, y: g.center_y }]
          return locs.flatMap((loc, li) => [
            <mesh key={`box-edge-${fi}-${li}`} position={[loc.x, depth / 2, -loc.y]} renderOrder={1}>
              <boxGeometry args={[g.width, depth, g.height]} />
              <meshBasicMaterial color={color} wireframe opacity={0.4} transparent depthWrite={false} />
            </mesh>,
          ])
        }

        if (g.shape === 'contour') {
          const isProfile = feature.type === 'profile'
          const depth = Math.abs(g.depth as number) || fallbackDepth
          // Both profiles and pockets use ContourMesh — profiles just get
          // different opacity (isProfile flag). This gives proper 3D shape
          // with filled faces, top/bottom outlines, and vertical edges.
          return [<ContourMesh key={`cnt-${fi}`} points={g.points} depth={depth} color={color} isProfile={isProfile} />]
        }

        if (g.shape === 'edge_strip') {
          return [<EdgeStripLine key={`edge-${fi}`} geom={g} color={color} />]
        }

        return []
      })}
    </group>
  )
}

// Feature types that REMOVE material (become holes punched through the solid block).
// Edge treatments (fillet/chamfer/taper) and faces are intentionally excluded.
const SOLID_HOLE_TYPE_RE = /pocket|hole|slot|through|thru|bore|drill|counterbore|countersink|tap/i

/**
 * Builds an extruded solid from the 2D feature data: the outer profile contour becomes a
 * filled block of `depth`, and through-pockets / drilled holes are punched out. Returns the
 * geometry in feature (CNC) coordinates extruded along +Z — the caller rotates it into scene
 * space. Approximate by design (blind-pocket depth and edge treatments are not modeled).
 */
function buildSolidFeatureGeometry(geometry: Record<string, unknown>, depth: number): THREE.ExtrudeGeometry | null {
  const features = (geometry.features ?? []) as Array<{ name?: string; type?: string; geometry?: any }>
  const profileFeat = features.find(
    (f) => f.type === 'profile' && f.geometry?.shape === 'contour' && Array.isArray(f.geometry.points) && f.geometry.points.length >= 3,
  )
  if (!profileFeat) return null

  const pts = profileFeat.geometry.points as Array<{ x: number; y: number }>
  const shape = new THREE.Shape()
  shape.moveTo(pts[0].x, pts[0].y)
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i].x, pts[i].y)
  shape.closePath()

  for (const f of features) {
    if (f === profileFeat) continue
    const g = f.geometry
    if (!g || !SOLID_HOLE_TYPE_RE.test(f.type ?? '')) continue
    if (g.shape === 'contour' && Array.isArray(g.points) && g.points.length >= 3) {
      const hole = new THREE.Path()
      hole.moveTo(g.points[0].x, g.points[0].y)
      for (let i = 1; i < g.points.length; i++) hole.lineTo(g.points[i].x, g.points[i].y)
      hole.closePath()
      shape.holes.push(hole)
    } else if (g.shape === 'cylinder') {
      const r = (g.diameter as number) / 2
      if (!(r > 0)) continue
      const locs: Array<{ x: number; y: number }> = g.locations || [{ x: g.center_x, y: g.center_y }]
      for (const loc of locs) {
        const hole = new THREE.Path()
        hole.absarc(loc.x, loc.y, r, 0, Math.PI * 2, true)
        shape.holes.push(hole)
      }
    } else if (g.shape === 'box') {
      const w = g.width as number, h = g.height as number
      const locs: Array<{ x: number; y: number }> = g.locations || [{ x: g.center_x, y: g.center_y }]
      for (const loc of locs) {
        const hole = new THREE.Path()
        hole.moveTo(loc.x - w / 2, loc.y - h / 2)
        hole.lineTo(loc.x + w / 2, loc.y - h / 2)
        hole.lineTo(loc.x + w / 2, loc.y + h / 2)
        hole.lineTo(loc.x - w / 2, loc.y + h / 2)
        hole.closePath()
        shape.holes.push(hole)
      }
    }
  }

  return new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: 48 })
}

/**
 * Solid (shaded) rendering of the 2D/DWG-extruded part — the "solid" half of the
 * solid/wireframe toggle. Falls back to the wireframe FeatureOverlay when there's no usable
 * outer profile to extrude. Scene mapping: extrude along +Z then rotateX(-90°) maps
 * (cnc_x, cnc_y, z) → (cnc_x, z, -cnc_y), matching cncToScene.
 */
function SolidFeatureMesh({ geometry }: { geometry: Record<string, unknown> }) {
  const partDepth = geometry.part_depth as number | undefined
  const stock = geometry.stock_dimensions as { z?: number } | undefined
  // Only extrude a solid once a real depth (Z) is known. Before the user answers
  // the part-depth question both are 0 — don't fabricate a height; fall back to
  // the flat 2D outline so the preview never shows a thickness the data lacks.
  const knownDepth = Math.abs(partDepth || stock?.z || 0)

  const geo = useMemo(
    () => (knownDepth > 0 ? buildSolidFeatureGeometry(geometry, knownDepth) : null),
    [geometry, knownDepth],
  )
  useEffect(() => () => { geo?.dispose() }, [geo])

  if (!geo) return <FeatureOverlay geometry={geometry} />

  return (
    <group rotation={[-Math.PI / 2, 0, 0]}>
      <mesh geometry={geo} renderOrder={0}>
        <meshPhongMaterial color="#9aa7b8" specular="#1e293b" shininess={18} side={THREE.DoubleSide} />
      </mesh>
      <lineSegments renderOrder={1}>
        <edgesGeometry args={[geo, 20]} />
        <lineBasicMaterial color="#0f172a" transparent opacity={0.5} />
      </lineSegments>
    </group>
  )
}

function FeatureDimLine({ from, to, color }: { from: [number, number, number]; to: [number, number, number]; color: string }) {
  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute([...from, ...to], 3))
    return g
  }, [from, to])

  return (
    <lineSegments geometry={geom}>
      <lineBasicMaterial color={color} opacity={0.5} transparent />
    </lineSegments>
  )
}

function FeatureLabels({ geometry, extent }: { geometry: Record<string, unknown>; extent: number }) {
  const features = (geometry.features ?? []) as Array<{
    name: string; type: string; geometry: any; notes?: string
  }>
  const partDepth = geometry.part_depth as number | undefined
  const stock = geometry.stock_dimensions as { z?: number } | undefined
  const fallbackDepth = partDepth || stock?.z || 0

  if (!features.length) return null

  return (
    <group>
      {features.map((feature, i) => {
        const g = feature.geometry
        if (!g?.shape || g.shape === 'face_plane') return null

        let x: number, y: number
        if (g.shape === 'contour') {
          const pts = g.points as Array<{ x: number; y: number }>
          if (!pts?.length) return null
          x = pts.reduce((s: number, p: { x: number; y: number }) => s + p.x, 0) / pts.length
          y = pts.reduce((s: number, p: { x: number; y: number }) => s + p.y, 0) / pts.length
        } else if (g.shape === 'edge_strip') {
          x = (g.start_x + g.end_x) / 2
          y = (g.start_y + g.end_y) / 2
        } else {
          x = g.center_x
          y = g.center_y
        }

        if (x == null || y == null) return null

        const color = featureColor(feature.type as FeatureType)
        const depth = Math.abs(g.depth as number) || fallbackDepth
        const elements: React.ReactNode[] = []

        // Diameter line across holes/bores
        if (g.shape === 'cylinder' && g.diameter) {
          const r = g.diameter / 2
          const locs: Array<{ x: number; y: number }> = g.locations || [{ x: g.center_x, y: g.center_y }]
          locs.forEach((loc: { x: number; y: number }, li: number) => {
            elements.push(
              <FeatureDimLine
                key={`diam-${i}-${li}`}
                from={[loc.x - r, depth + extent * 0.02, -loc.y]}
                to={[loc.x + r, depth + extent * 0.02, -loc.y]}
                color={color}
              />,
            )
          })
        }

        // Width/height lines on boxes
        if (g.shape === 'box' && g.width && g.height) {
          const locs: Array<{ x: number; y: number }> = g.locations || [{ x: g.center_x, y: g.center_y }]
          const hw = g.width / 2
          const hh = g.height / 2
          locs.forEach((loc: { x: number; y: number }, li: number) => {
            // Width line
            elements.push(
              <FeatureDimLine
                key={`w-${i}-${li}`}
                from={[loc.x - hw, depth + extent * 0.02, -loc.y + hh]}
                to={[loc.x + hw, depth + extent * 0.02, -loc.y + hh]}
                color={color}
              />,
            )
            // Height line
            elements.push(
              <FeatureDimLine
                key={`h-${i}-${li}`}
                from={[loc.x + hw, depth + extent * 0.02, -loc.y - hh]}
                to={[loc.x + hw, depth + extent * 0.02, -loc.y + hh]}
                color={color}
              />,
            )
          })
        }

        return <group key={`feat-${i}`}>{elements}</group>
      })}
    </group>
  )
}

// ---- Dimension annotation line with label ----

function DimLine({ start, end, label, offset, color = '#94a3b8' }: {
  start: THREE.Vector3
  end: THREE.Vector3
  label: string
  offset: THREE.Vector3
  color?: string
}) {
  const s = useMemo(() => start.clone().add(offset), [start, offset])
  const e = useMemo(() => end.clone().add(offset), [end, offset])
  const mid = useMemo(() => s.clone().add(e).multiplyScalar(0.5), [s, e])

  // Extension lines from original points to offset dimension line
  const extGeom = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute([
      start.x, start.y, start.z, s.x, s.y, s.z,
      end.x, end.y, end.z, e.x, e.y, e.z,
    ], 3))
    return g
  }, [start, end, s, e])

  // Main dimension line
  const dimGeom = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute([
      s.x, s.y, s.z, e.x, e.y, e.z,
    ], 3))
    return g
  }, [s, e])

  const dist = s.distanceTo(e)

  return (
    <group>
      <lineSegments geometry={extGeom}>
        <lineBasicMaterial color={color} opacity={0.3} transparent />
      </lineSegments>
      <lineSegments geometry={dimGeom}>
        <lineBasicMaterial color={color} opacity={0.5} transparent />
      </lineSegments>
      <Html position={[mid.x, mid.y, mid.z]} distanceFactor={dist * 2.5} center>
        <div className="px-1.5 py-0.5 rounded bg-[#030712]/80 pointer-events-none whitespace-nowrap">
          <span className="font-mono text-[10px] text-slate-300">{label}</span>
        </div>
      </Html>
    </group>
  )
}

function DimensionAnnotations({ geometry, footprint }: {
  geometry: Record<string, unknown>
  footprint?: { center: { x: number; y: number }; min: { x: number; y: number }; max: { x: number; y: number } } | null
}) {
  const env = geometry.part_envelope as { min_x: number; min_y: number; max_x: number; max_y: number } | undefined
  const stock = geometry.stock_dimensions as { x?: number; y?: number; z?: number } | undefined
  const partDepth = geometry.part_depth as number | undefined
  const sz = partDepth || stock?.z || 0

  // Preferred path: build scene-space bounds from the ACTUAL feature footprint, so the
  // dimension lines hug the rendered part (the extractor's feature frame is offset from
  // the envelope frame). scene = [cnc_x, depth, -cnc_y]. Reuse the mesh dimension layout.
  if (footprint) {
    const bounds = {
      min: [footprint.min.x, 0, -footprint.max.y] as [number, number, number],
      max: [footprint.max.x, sz, -footprint.min.y] as [number, number, number],
    }
    return <MeshDimensionAnnotations bounds={bounds} />
  }

  // Fallback (no positioned features): envelope-centered at origin, as before.
  if (!env) return null
  const sx = stock?.x || (env.max_x - env.min_x)
  const sy = stock?.y || (env.max_y - env.min_y)
  const gap = Math.max(sx, sy) * 0.08

  // X dimension — along front edge (positive Z in scene = negative Y in CNC)
  const xStart = new THREE.Vector3(-sx / 2, 0, sy / 2)
  const xEnd = new THREE.Vector3(sx / 2, 0, sy / 2)
  const xOffset = new THREE.Vector3(0, 0, gap)

  // Y dimension — along right edge
  const yStart = new THREE.Vector3(sx / 2, 0, sy / 2)
  const yEnd = new THREE.Vector3(sx / 2, 0, -sy / 2)
  const yOffset = new THREE.Vector3(gap, 0, 0)

  // Z dimension — vertical along front-right corner
  const zStart = new THREE.Vector3(sx / 2 + gap, 0, sy / 2)
  const zEnd = new THREE.Vector3(sx / 2 + gap, sz, sy / 2)
  const zOffset = new THREE.Vector3(0, 0, gap * 0.5)

  const xLen = +(env.max_x - env.min_x).toFixed(1)
  const yLen = +(env.max_y - env.min_y).toFixed(1)

  return (
    <group>
      <DimLine start={xStart} end={xEnd} label={`${xLen} mm`} offset={xOffset} />
      <DimLine start={yStart} end={yEnd} label={`${yLen} mm`} offset={yOffset} />
      {sz > 0 && (
        <DimLine start={zStart} end={zEnd} label={`${+sz.toFixed(1)} mm`} offset={zOffset} color="#3b82f6" />
      )}
    </group>
  )
}

function MeshDimensionAnnotations({ bounds }: {
  bounds: { min: [number, number, number]; max: [number, number, number] } | null
}) {
  if (!bounds) return null
  const [minX, minY, minZ] = bounds.min
  const [maxX, maxY, maxZ] = bounds.max

  const xLen = +(maxX - minX).toFixed(1)
  const yLen = +(maxY - minY).toFixed(1)
  const zLen = +(maxZ - minZ).toFixed(1)
  if (xLen < 0.1 && yLen < 0.1 && zLen < 0.1) return null

  const gap = Math.max(xLen, yLen, zLen) * 0.08

  // X dimension — along front-bottom edge
  const xStart = new THREE.Vector3(minX, minY, maxZ)
  const xEnd = new THREE.Vector3(maxX, minY, maxZ)
  const xOffset = new THREE.Vector3(0, 0, gap)

  // Z dimension — along right-bottom edge
  const zStart = new THREE.Vector3(maxX, minY, minZ)
  const zEnd = new THREE.Vector3(maxX, minY, maxZ)
  const zOffset = new THREE.Vector3(gap, 0, 0)

  // Y dimension — vertical along front-right corner
  const yStart = new THREE.Vector3(maxX + gap, minY, maxZ)
  const yEnd = new THREE.Vector3(maxX + gap, maxY, maxZ)
  const yOffset = new THREE.Vector3(0, 0, gap * 0.5)

  return (
    <group>
      <DimLine start={xStart} end={xEnd} label={`${xLen} mm`} offset={xOffset} />
      <DimLine start={zStart} end={zEnd} label={`${zLen} mm`} offset={zOffset} />
      {yLen > 0.1 && (
        <DimLine start={yStart} end={yEnd} label={`${yLen} mm`} offset={yOffset} color="#3b82f6" />
      )}
    </group>
  )
}

export function DatumOrigin({ geometry, extent, position, register }: {
  geometry: Record<string, unknown>
  extent: number
  position?: [number, number, number]
  register?: string
}) {
  const len = Math.max(extent * 0.1, 5)
  const datumLabel = (geometry.datum_position as string) || 'Origin'
  const reg = register ?? 'G54'

  const lineGeom = useMemo(() => {
    const positions = [
      0, 0, 0, len, 0, 0,
      0, 0, 0, 0, 0, -len,
      0, 0, 0, 0, len, 0,
    ]
    const colors = [
      1, 0, 0, 1, 0, 0,
      0, 0.8, 0, 0, 0.8, 0,
      0, 0.4, 1, 0, 0.4, 1,
    ]
    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
    return geom
  }, [len])

  return (
    <group position={position ?? [0, 0, 0]}>
      <lineSegments geometry={lineGeom}>
        <lineBasicMaterial vertexColors />
      </lineSegments>
      <mesh position={[len, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <coneGeometry args={[len * 0.05, len * 0.15, 8]} />
        <meshBasicMaterial color="#ef4444" />
      </mesh>
      <mesh position={[0, 0, -len]} rotation={[-Math.PI / 2, 0, 0]}>
        <coneGeometry args={[len * 0.05, len * 0.15, 8]} />
        <meshBasicMaterial color="#22c55e" />
      </mesh>
      <mesh position={[0, len, 0]}>
        <coneGeometry args={[len * 0.05, len * 0.15, 8]} />
        <meshBasicMaterial color="#3b82f6" />
      </mesh>
      <Html position={[len + 2, 0, 0]} distanceFactor={extent * 0.5} center>
        <span className="font-mono text-[9px] text-red-400 font-bold pointer-events-none">X</span>
      </Html>
      <Html position={[0, 0, -(len + 2)]} distanceFactor={extent * 0.5} center>
        <span className="font-mono text-[9px] text-green-400 font-bold pointer-events-none">Y</span>
      </Html>
      <Html position={[0, len + 2, 0]} distanceFactor={extent * 0.5} center>
        <span className="font-mono text-[9px] text-blue-400 font-bold pointer-events-none">Z</span>
      </Html>
      <Html position={[len * 0.5, 0, len * 0.3]} distanceFactor={extent * 0.6} center>
        <div className="px-2 py-1 rounded border border-amber-500/30 bg-[#030712]/70 backdrop-blur-sm pointer-events-none whitespace-nowrap">
          <span className="font-mono text-[10px] text-amber-300 font-medium">{reg}</span>
          <span className="font-mono text-[9px] text-slate-400 ml-1.5">{datumLabel}</span>
        </div>
      </Html>
    </group>
  )
}

function CameraSnap({
  view,
  viewKey,
  center,
  extent,
}: {
  view: PlaneView
  viewKey: number
  center: THREE.Vector3
  extent: number
}) {
  const { camera } = useThree()
  const controlsRef = useRef<any>(null)

  useEffect(() => {
    const d = extent * 1.5
    let pos: THREE.Vector3

    switch (view) {
      case 'top':
        pos = new THREE.Vector3(center.x, center.y + d, center.z)
        break
      case 'front':
        pos = new THREE.Vector3(center.x, center.y, center.z + d)
        break
      case 'right':
        pos = new THREE.Vector3(center.x + d, center.y, center.z)
        break
      case 'iso':
      default:
        pos = new THREE.Vector3(center.x + d * 0.6, center.y + d * 0.8, center.z + d * 0.6)
        break
    }

    camera.position.copy(pos)
    camera.lookAt(center)
    camera.updateProjectionMatrix()
    if (controlsRef.current) controlsRef.current.update()
  }, [view, viewKey, center, extent, camera])

  return <OrbitControls ref={controlsRef} target={center} makeDefault />
}

// ---- Exported scene (for embedding in any Canvas) ----

export function GeometryPreviewScene({
  geometry,
  activeView,
  viewKey,
  showStock = false,
  wireframe = false,
  stepMeshes,
  pickerActive,
  snapPoints,
  hoveredPosition,
  selectedPosition,
  onSnapHover,
  onSnapClick,
  workOffsetRegister,
  workOffsets,
  controls = 'orbit',
}: {
  geometry: Record<string, unknown>
  activeView: PlaneView
  viewKey: number
  showStock?: boolean
  /** Solid (shaded) vs wireframe (edges-only) rendering of the part. Default solid. */
  wireframe?: boolean
  stepMeshes?: OcctMesh[] | null
  pickerActive?: boolean
  snapPoints?: import('../../lib/snap-points').SnapPoint[]
  hoveredPosition?: [number, number, number] | null
  selectedPosition?: [number, number, number] | null
  onSnapHover?: (pos: { x: number; y: number; z: number } | null, snapId: string | null) => void
  onSnapClick?: (pos: { x: number; y: number; z: number }, snapId: string | null) => void
  workOffsetRegister?: string
  workOffsets?: Array<{ register: string; x: number; y: number; z: number }>
  /**
   * Camera controls rendered inside the scene. `'orbit'` (default) renders the legacy
   * OrbitControls + CameraSnap — this is what the toolpath viewer relies on, so it must
   * stay the default. The agent-flow wrapper passes `'none'` and supplies its own CameraRig.
   */
  controls?: 'orbit' | 'none'
}) {
  const env = geometry.part_envelope as { min_x: number; min_y: number; max_x: number; max_y: number; min_z?: number; max_z?: number } | undefined
  const stock = geometry.stock_dimensions as { x?: number; y?: number; z?: number } | undefined
  const sx = stock?.x || (env ? env.max_x - env.min_x : 100)
  const sy = stock?.y || (env ? env.max_y - env.min_y : 100)
  const sz = stock?.z || 10

  // When STEP meshes exist, compute bounds from actual vertex data instead of 2D feature data
  const meshBounds = useMemo(() => {
    if (stepMeshes && stepMeshes.length > 0) return computeMeshBounds(stepMeshes)
    return null
  }, [stepMeshes])

  // 2D path: actual footprint of the features, so the stock box AND dimension lines sit
  // over the part (the extractor's feature frame is offset from the envelope frame).
  const footprint = useMemo(() => computeFeatureFootprint(geometry), [geometry])

  const center = useMemo(() => {
    if (meshBounds) return new THREE.Vector3(...meshBounds.center)
    return new THREE.Vector3(0, sz / 2, 0)
  }, [meshBounds, sz])

  const extent = useMemo(() => {
    if (meshBounds) return meshBounds.extent
    return Math.max(sx, sy, sz, 20)
  }, [meshBounds, sx, sy, sz])

  // Request a frame when scene content changes (harmless under frameloop="always").
  const { invalidate: inv } = useThree()
  useEffect(() => { inv() }, [geometry, stepMeshes, activeView, viewKey, showStock, wireframe, inv])

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[50, 80, 50]} intensity={0.6} />
      <directionalLight position={[-40, -60, -30]} intensity={0.3} />
      {controls === 'orbit' && (
        <CameraSnap view={activeView} viewKey={viewKey} center={center} extent={extent} />
      )}
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport labelColor="white" axisHeadScale={0.8} />
      </GizmoHelper>
      {/* Grid removed — caused z-fighting with feature outlines */}
      {/* `fitTarget` tags the actual part geometry so CameraRig frames it precisely —
          dimension leaders/labels are kept OUT of the group (they use the part_envelope
          frame, which can differ from the geometry frame, and would skew the fit). */}
      {stepMeshes && stepMeshes.length > 0 ? (
        <>
          <group userData={{ fitTarget: true }}>
            <StepMesh stepMeshes={stepMeshes} wireframe={wireframe} />
            {showStock && <StockBox geometry={geometry} meshCenter={meshBounds?.center ?? null} />}
          </group>
          <MeshDimensionAnnotations bounds={meshBounds} />
        </>
      ) : (
        <>
          <group userData={{ fitTarget: true }}>
            {showStock && <StockBox geometry={geometry} footprintCenter={footprint?.center ?? null} />}
            {wireframe ? <FeatureOverlay geometry={geometry} /> : <SolidFeatureMesh geometry={geometry} />}
          </group>
          <FeatureLabels geometry={geometry} extent={extent} />
          <DimensionAnnotations geometry={geometry} footprint={footprint} />
        </>
      )}
      {/* Always render confirmed work offsets */}
      {workOffsets && workOffsets.map((wo, i) => (
        <DatumOrigin
          key={`${wo.register}-${i}`}
          geometry={geometry}
          extent={extent}
          position={cncToScene(wo, sz)}
          register={wo.register}
        />
      ))}
      {/* Live picker preview (hovered or selected, before confirm) */}
      {(selectedPosition || hoveredPosition) && (
        <DatumOrigin
          geometry={geometry}
          extent={extent}
          position={selectedPosition ?? hoveredPosition ?? undefined}
          register={workOffsetRegister}
        />
      )}
      {pickerActive && snapPoints && onSnapHover && onSnapClick && (
        <Suspense fallback={null}>
          <SnapPointsLayer
            geometry={geometry}
            snapPoints={snapPoints}
            extent={extent}
            onHover={onSnapHover}
            onClick={onSnapClick}
          />
        </Suspense>
      )}
    </>
  )
}

// ---- Standalone component with Canvas + view buttons ----

interface GeometryPreviewProps {
  geometry: Record<string, unknown>
  height?: string
  showViewButtons?: boolean
  stepFile?: File | null
  /** Current work offsets to display as DatumOrigin gizmos */
  workOffsets?: Array<{ register: string; x: number; y: number; z: number }>
  /** Called when user confirms a work offset via the 3D picker */
  onWorkOffsetChange?: (offset: { register: string; x: number; y: number; z: number }) => void
  /** Called when STEP mesh bounds are computed from tessellation */
  onMeshBoundsChange?: (bounds: { min: [number, number, number]; max: [number, number, number] } | null) => void
}

export default function GeometryPreview({
  geometry,
  height = '300px',
  showViewButtons = true,
  stepFile,
  workOffsets: externalWorkOffsets,
  onWorkOffsetChange,
  onMeshBoundsChange,
}: GeometryPreviewProps) {
  const [activeView, setActiveView] = useState<PlaneView>('iso')
  const [viewKey, setViewKey] = useState(0)
  const [showStock, setShowStock] = useState(false)
  // false = solid (default), true = wireframe
  const [wireframe, setWireframe] = useState(false)
  const [stepMeshes, setStepMeshes] = useState<OcctMesh[] | null>(null)
  const [tessellating, setTessellating] = useState(false)

  // Meshes to render: STEP tessellation if present, else a backend-built solid
  // mesh on the geometry (e.g. a turned DWG part reconstructed by the CAM service).
  const renderMeshes = useMemo<OcctMesh[] | null>(() => {
    if (stepMeshes && stepMeshes.length > 0) return stepMeshes
    const sm = geometry?.solid_mesh as { vertices: number[]; indices: number[]; normals?: number[] } | undefined
    if (sm && sm.vertices?.length >= 9 && sm.indices?.length >= 3) return [solidMeshToOcct(sm)]
    return null
  }, [stepMeshes, geometry])

  // Work-offset picker state (right-click → candidate → confirm)
  const [register, setRegister] = useState<WorkOffsetRegister>('G54')
  const [candidate, setCandidate] = useState<{ position: { x: number; y: number; z: number }; label: string } | null>(null)
  const [popupAnchor, setPopupAnchor] = useState<{ x: number; y: number } | null>(null)
  const [maximized, setMaximized] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Tessellate STEP file internally — typed arrays never leave this component
  useEffect(() => {
    if (!stepFile) {
      setStepMeshes(null)
      return
    }
    let cancelled = false
    setTessellating(true)
    stepFile.arrayBuffer().then(async (buf) => {
      const occt = await occtimportjs({
        locateFile: (name: string) => `/${name}`,
      })
      const result = occt.ReadStepFile(new Uint8Array(buf), null)
      if (!cancelled && result.success) {
        setStepMeshes(result.meshes as OcctMesh[])
      }
      if (!cancelled) setTessellating(false)
    }).catch((err) => {
      console.warn('STEP tessellation failed:', err)
      if (!cancelled) {
        setStepMeshes(null)
        setTessellating(false)
      }
    })
    return () => { cancelled = true }
  }, [stepFile])

  const handleViewChange = useCallback((v: PlaneView) => {
    setActiveView(v)
    setViewKey(k => k + 1)
  }, [])

  const env = geometry.part_envelope as { min_x: number; min_y: number; max_x: number; max_y: number } | undefined
  const stock = geometry.stock_dimensions as { x?: number; y?: number; z?: number } | undefined
  const sx = stock?.x || (env ? env.max_x - env.min_x : 100)
  const sy = stock?.y || (env ? env.max_y - env.min_y : 100)
  const sz = stock?.z || 10

  // Snap points for work offset picker
  const snapPoints = useMemo(() => {
    if (!geometry) return []
    return computeSnapPoints(geometry)
  }, [geometry])

  // Right-click the part → resolve a candidate origin (snap to feature, else free point)
  const handlePartContext = useCallback((e: ThreeEvent<MouseEvent>) => {
    if (!onWorkOffsetChange) return
    e.stopPropagation()
    e.nativeEvent.preventDefault()
    const cnc = sceneToCnc(e.point, sz)
    const threshold = Math.max(sx, sy) * 0.08
    const res = resolveSnapPoint(cnc, snapPoints, env, threshold)
    const rect = containerRef.current?.getBoundingClientRect()
    setCandidate({ position: res.position, label: res.label })
    setPopupAnchor({
      x: rect ? e.nativeEvent.clientX - rect.left : 0,
      y: rect ? e.nativeEvent.clientY - rect.top : 0,
    })
  }, [onWorkOffsetChange, sz, sx, sy, snapPoints, env])

  const handleConfirmOrigin = useCallback(() => {
    if (!candidate || !onWorkOffsetChange) return
    onWorkOffsetChange({ register, x: candidate.position.x, y: candidate.position.y, z: candidate.position.z })
    // Auto-advance to the next register for the following pick
    const idx = REGISTERS.indexOf(register)
    if (idx < REGISTERS.length - 1) setRegister(REGISTERS[idx + 1])
    setCandidate(null)
    setPopupAnchor(null)
  }, [candidate, register, onWorkOffsetChange])

  const handleCancelOrigin = useCallback(() => {
    setCandidate(null)
    setPopupAnchor(null)
  }, [])

  // Esc exits fullscreen (the popup handles its own Esc while open)
  useEffect(() => {
    if (!maximized) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !popupAnchor) setMaximized(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [maximized, popupAnchor])

  // (moved above — declared before callbacks that reference them)

  // Use mesh bounds when available, fall back to 2D feature data
  const outerBounds = useMemo(() => {
    if (renderMeshes && renderMeshes.length > 0) return computeMeshBounds(renderMeshes)
    return null
  }, [renderMeshes])

  // Report mesh bounds to parent when computed
  useEffect(() => {
    if (outerBounds && onMeshBoundsChange) {
      onMeshBoundsChange({ min: outerBounds.min, max: outerBounds.max })
    } else if (!outerBounds && onMeshBoundsChange) {
      onMeshBoundsChange(null)
    }
  }, [outerBounds, onMeshBoundsChange])

  const geoExtent = outerBounds ? outerBounds.extent : Math.max(sx, sy, sz, 20)
  const geoCamDist = geoExtent * 1.5
  // Initial camera position only — CameraRig re-fits to the real part bounds on mount,
  // so this is just a sane starting pose (the part_envelope frame is unreliable here).
  const geoCenter: [number, number, number] = outerBounds ? outerBounds.center : [0, sz / 2, 0]

  // Stable refs to avoid re-creating Canvas props on every render
  const canvasStyle = useMemo(() => ({ background: '#030712', borderRadius: '0.5rem' }), [])
  const cameraConfig = useMemo(() => ({
    position: [geoCenter[0] + geoCamDist * 0.6, geoCenter[1] + geoCamDist * 0.8, geoCenter[2] + geoCamDist * 0.6] as [number, number, number],
    fov: 45,
    near: 0.1,
    far: geoExtent * 20,
  }), [geoCenter[0], geoCenter[1], geoCenter[2], geoCamDist, geoExtent])
  const handleCreated = useCallback(({ gl, invalidate }: { gl: THREE.WebGLRenderer; invalidate: () => void }) => {
    const canvas = gl.domElement
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault()
      console.warn('[GeometryPreview] WebGL context lost — will restore')
    })
    canvas.addEventListener('webglcontextrestored', () => {
      console.log('[GeometryPreview] WebGL context restored')
      invalidate()
    })
  }, [])

  return (
    <>
      <AnimatePresence>
        {maximized && (
          <motion.div
            key="origin-fs-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.32 }}
            className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm"
            onClick={() => setMaximized(false)}
          />
        )}
      </AnimatePresence>

      <motion.div
        ref={containerRef}
        layout
        transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
        className={maximized ? 'fixed inset-4 z-50 overflow-hidden rounded-xl' : 'relative'}
        style={maximized ? undefined : { height }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <Canvas
          frameloop="always"
          // Measure via offsetWidth/Height, NOT getBoundingClientRect. The container uses
          // framer-motion's `layout` prop, which animates size with a CSS transform:scale().
          // getBoundingClientRect() includes that transform, so r3f would latch onto a
          // mid-animation scaled-down size (e.g. 794px for a ~1100px panel) and never
          // re-measure — leaving the camera framed for the wrong size (off-center part) and
          // the gizmo in the wrong corner. offsetSize ignores transforms → true size.
          resize={{ offsetSize: true }}
          camera={cameraConfig}
          style={canvasStyle}
          onCreated={handleCreated}
        >
          <GeometryPreviewScene
            geometry={geometry}
            activeView={activeView}
            viewKey={viewKey}
            showStock={showStock}
            wireframe={wireframe}
            stepMeshes={renderMeshes}
            controls="none"
            selectedPosition={candidate ? cncToScene(candidate.position, sz) : undefined}
            workOffsetRegister={register}
            workOffsets={externalWorkOffsets}
          />
          <CameraRig
            view={activeView}
            viewKey={viewKey}
            geometry={geometry}
            stepMeshes={renderMeshes}
            showStock={showStock}
          />
          {/* Invisible plane: right-click anywhere on the part to place a candidate origin.
              Left-drag passes through to CameraRig, so it only ever orbits.
              userData.excludeFromFit keeps this oversized plane out of the camera fit box. */}
          {onWorkOffsetChange && (
            <mesh
              position={cncToScene({ x: env ? (env.min_x + env.max_x) / 2 : 0, y: env ? (env.min_y + env.max_y) / 2 : 0, z: 0 }, sz)}
              rotation={[-Math.PI / 2, 0, 0]}
              onContextMenu={handlePartContext}
              userData={{ excludeFromFit: true }}
            >
              <planeGeometry args={[sx * 1.5, sy * 1.5]} />
              <meshBasicMaterial visible={false} />
            </mesh>
          )}
        </Canvas>

        {/* Right-click origin popup — does the whole interaction (snap label, register, confirm) */}
        {popupAnchor && candidate && (
          <OriginPickerPopup
            anchor={popupAnchor}
            snapLabel={candidate.label}
            position={candidate.position}
            register={register}
            onRegisterChange={setRegister}
            onConfirm={handleConfirmOrigin}
            onCancel={handleCancelOrigin}
          />
        )}

        {/* Confirmed offsets readout */}
        {externalWorkOffsets && externalWorkOffsets.length > 0 && (
          <div className="absolute left-2 top-2 z-20 space-y-0.5 rounded-lg border border-cyan-500/15 bg-[#030712]/85 px-2.5 py-1.5 backdrop-blur-sm">
            {externalWorkOffsets.map((wo) => (
              <div key={wo.register} className="font-mono text-[9px] text-slate-400">
                <span className={wo.register === register ? 'font-bold text-amber-300' : 'text-slate-300'}>{wo.register}</span>
                {' '}X{wo.x.toFixed(1)} Y{wo.y.toFixed(1)} Z{wo.z.toFixed(1)}
              </div>
            ))}
          </div>
        )}

        {/* Discoverability hint */}
        {onWorkOffsetChange && !candidate && (
          <div className="pointer-events-none absolute bottom-3 left-3 z-20 rounded-lg border border-cyan-500/20 bg-[#030712]/85 px-3 py-1.5 backdrop-blur-sm">
            <span className="font-mono text-[10px] text-slate-400">Right-click the part to set work origin</span>
          </div>
        )}

        {tessellating && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="animate-pulse font-mono text-xs text-slate-500">Tessellating STEP...</span>
          </div>
        )}

        {showViewButtons && (
          <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
            {(['iso', 'top', 'front', 'right'] as const).map((v) => (
              <button
                key={v}
                onClick={() => handleViewChange(v)}
                className={`
                  px-2 py-1 rounded-lg font-mono text-[10px] tracking-widest uppercase
                  border transition-colors duration-150
                  ${activeView === v
                    ? 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30'
                    : 'text-slate-500 border-cyan-500/10 bg-[#030712]/80 backdrop-blur-sm hover:text-slate-300 hover:border-cyan-500/25'
                  }
                `}
              >
                {v === 'iso' ? 'ISO' : v === 'top' ? 'TOP' : v === 'front' ? 'FRONT' : 'RIGHT'}
              </button>
            ))}
            <button
              onClick={() => setShowStock((s) => !s)}
              className={`
                mt-1 px-2 py-1 rounded-lg font-mono text-[10px] tracking-widest uppercase
                border transition-colors duration-150
                ${showStock
                  ? 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30'
                  : 'text-slate-500 border-cyan-500/10 bg-[#030712]/80 backdrop-blur-sm hover:text-slate-300 hover:border-cyan-500/25'
                }
              `}
            >
              STOCK
            </button>
            <button
              onClick={() => setWireframe((w) => !w)}
              title={wireframe ? 'Showing wireframe — click for solid' : 'Showing solid — click for wireframe'}
              className={`
                px-2 py-1 rounded-lg font-mono text-[10px] tracking-widest uppercase
                border transition-colors duration-150
                ${wireframe
                  ? 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30'
                  : 'text-slate-500 border-cyan-500/10 bg-[#030712]/80 backdrop-blur-sm hover:text-slate-300 hover:border-cyan-500/25'
                }
              `}
            >
              WIRE
            </button>
            <button
              onClick={() => setMaximized((m) => !m)}
              title={maximized ? 'Exit fullscreen' : 'Fullscreen'}
              className="mt-1 flex items-center justify-center rounded-lg border border-cyan-500/10 bg-[#030712]/80 px-2 py-1 text-slate-400 backdrop-blur-sm transition-colors hover:border-cyan-500/25 hover:text-slate-200"
            >
              {maximized ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
            </button>
          </div>
        )}
      </motion.div>
    </>
  )
}
